import type {UserSolution, GradingResult} from 'iq-takehomes/types';
import {UserFileDownloadLink} from 'iq-takehomes/components/user_file_download_link';
import Link from 'next/link';

const DIMENSIONS: Array<{key: keyof GradingResult; label: string}> = [
  {key: 'business_intuition', label: 'Business Intuition'},
  {key: 'general_communication', label: 'Communication'},
  {key: 'technical_sense', label: 'Technical Sense'},
  {key: 'solution_correctness', label: 'Correctness'},
  {key: 'code_quality', label: 'Code Quality'},
];

function scoreColor(score: number) {
  if (score >= 8) return 'bg-green-500';
  if (score >= 5) return 'bg-yellow-400';
  return 'bg-red-400';
}

function ScoreBar({score}: {score: number}) {
  const pct = Math.round((score / 10) * 100);
  return (
    <div className='flex items-center gap-2 flex-1'>
      <div className='flex-1 h-2 bg-iq-grey-light rounded-full overflow-hidden'>
        <div className={`h-full rounded-full ${scoreColor(score)}`} style={{width: `${pct}%`}} />
      </div>
      <span className='text-sm font-semibold w-10 text-right text-iq-grey-dark tabular-nums'>
        {Math.round(score)}<span className='text-iq-grey-medium font-normal'>/10</span>
      </span>
    </div>
  );
}

type Props = {
  solution: UserSolution;
  slug: string;
  onResubmit?: () => void;
};

export function MySolutionPanel({solution, slug, onResubmit}: Props) {
  const {grading_result, overall_score, s3_keys, s3_urls, created_at} = solution;
  if (!grading_result) return null;

  const submittedAt = new Date(created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div className='bg-white border border-iq-grey-light rounded-xl p-5 flex flex-col gap-4'>
      <div className='flex items-center justify-between gap-2'>
        <h3 className='text-lg font-semibold text-iq-black'>Your Scores</h3>
        <span className='text-xs font-semibold px-2 py-0.5 bg-green-100 text-green-700 rounded-full'>Graded</span>
      </div>

      {overall_score != null && (
        <div className='flex items-center gap-3 pb-3 border-b border-iq-grey-light'>
          <span className='text-3xl font-bold text-iq-black tabular-nums'>
            {overall_score.toFixed(1)}
          </span>
          <div>
            <p className='text-sm text-iq-grey-medium leading-tight'>/10 overall</p>
          </div>
        </div>
      )}

      <div className='flex flex-col gap-2.5'>
        {DIMENSIONS.map(({key, label}) => {
          const dim = grading_result[key as keyof GradingResult] as {score: number};
          return (
            <div key={key} className='flex items-center gap-2'>
              <span className='text-sm text-iq-grey-medium w-28 shrink-0'>{label}</span>
              <ScoreBar score={dim.score} />
            </div>
          );
        })}
      </div>

      {grading_result.overall_feedback && (
        <p className='text-sm text-iq-grey-medium leading-relaxed line-clamp-4'>
          {grading_result.overall_feedback}
        </p>
      )}

      <div className='flex flex-col gap-2 pt-1 border-t border-iq-grey-light'>
        <p className='text-sm text-iq-grey-medium'>
          <span className='font-semibold text-iq-grey-dark'>Latest submission</span> · {submittedAt}
        </p>
        {s3_keys.length > 0 && (
          <ul className='flex flex-col gap-1.5'>
            {s3_keys.map((key, i) => {
              const url = s3_urls[i];
              if (!url) return null;
              return (
                <li key={key}>
                  <UserFileDownloadLink s3Key={key} url={url} />
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <Link
        href={`/takehomes/${slug}/solutions/${solution.id}`}
        className='w-full py-2.5 bg-iq-black text-white text-sm font-semibold rounded-lg text-center hover:opacity-80 transition-opacity'>
        View full grading report
      </Link>

      {onResubmit && (
        <button
          type='button'
          onClick={onResubmit}
          className='w-full py-2.5 border border-iq-grey-light text-iq-black text-sm font-semibold rounded-lg hover:bg-iq-grey-lighter transition-colors'>
          Submit another solution
        </button>
      )}
    </div>
  );
}
